import { useCallback, useEffect, useMemo, useState } from "react"
import type { GanttTask, GanttDependency } from "../gantt-chart/data/types"
import {
  loadGanttProject,
  saveGanttTask,
  saveGanttTasks,
  deleteGanttTask,
  createGanttDependency,
  updateGanttDependency,
  deleteGanttDependency,
} from "../gantt-chart/data/ganttDatabaseService"
import { toDate, toDateStr, daysBetween, recomputeSchedule, wouldCreateCycle, PHASE_PALETTE } from "./gantt-utils"
import { addWorkingDays, countWorkingDays, DEFAULT_WORKING_DAYS, type WorkingDaysConfig } from "../schedule-date-engine"

/**
 * Single owner of Gantt's data + every mutation (load, CRUD, dependencies,
 * reorder, bulk actions, baseline). Instantiated ONCE in scheduling-page.tsx
 * and passed down to both the Gantt timeline (Gantt.tsx) and List View
 * (GanttListView.tsx) so they never fetch independently or drift out of
 * sync -- one source of truth per project, same as every other KrewUp
 * feature's store.
 */
export function useGanttData({
  projectId,
  companyId,
  userId,
  workingDays = DEFAULT_WORKING_DAYS,
}: {
  projectId?: string
  companyId?: string | null
  userId?: string
  workingDays?: WorkingDaysConfig
}) {
  const [tasks, setTasks] = useState<GanttTask[]>([])
  const [dependencies, setDependencies] = useState<GanttDependency[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    if (!projectId) {
      setTasks([])
      setDependencies([])
      setLoading(false)
      return
    }
    setLoading(true)
    loadGanttProject(projectId).then((project) => {
      if (cancelled) return
      setTasks(project?.tasks.filter((t) => t.type !== "subtask") ?? [])
      setDependencies(project?.dependencies ?? [])
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [projectId])

  const persistTasks = useCallback(
    (list: GanttTask[]) => {
      if (!projectId || !companyId || !userId || list.length === 0) return
      saveGanttTasks(projectId, list, companyId, userId).catch((err) => console.error("[Gantt] persistTasks failed", err))
    },
    [projectId, companyId, userId]
  )

  const tasksById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks])

  const phases = useMemo(
    () => tasks.filter((t) => t.type === "group").sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [tasks]
  )
  const phaseColor = useCallback(
    (phaseId: string) => PHASE_PALETTE[phases.findIndex((p) => p.id === phaseId) % PHASE_PALETTE.length] ?? PHASE_PALETTE[0],
    [phases]
  )

  const childrenByPhase = useMemo(() => {
    const map = new Map<string, GanttTask[]>()
    for (const t of tasks) {
      if (t.type !== "task" || !t.parentId) continue
      if (!map.has(t.parentId)) map.set(t.parentId, [])
      map.get(t.parentId)!.push(t)
    }
    Array.from(map.values()).forEach((list) => list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)))
    return map
  }, [tasks])

  const phaseSummaries = useMemo(() => {
    const map = new Map<string, { start: string; end: string; progress: number; count: number }>()
    for (const phase of phases) {
      const children = childrenByPhase.get(phase.id) || []
      if (children.length === 0) {
        map.set(phase.id, { start: phase.startDate, end: phase.endDate, progress: phase.progress, count: 0 })
        continue
      }
      const starts = children.map((t) => toDate(t.startDate).getTime())
      const ends = children.map((t) => toDate(t.endDate).getTime())
      const totalDuration = children.reduce((sum, t) => sum + (daysBetween(toDate(t.startDate), toDate(t.endDate)) + 1), 0)
      const weightedProgress =
        totalDuration === 0
          ? 0
          : children.reduce((sum, t) => sum + t.progress * (daysBetween(toDate(t.startDate), toDate(t.endDate)) + 1), 0) / totalDuration
      map.set(phase.id, {
        start: toDateStr(new Date(Math.min(...starts))),
        end: toDateStr(new Date(Math.max(...ends))),
        progress: Math.round(weightedProgress),
        count: children.length,
      })
    }
    return map
  }, [phases, childrenByPhase])

  const phaseName = useCallback((phaseId?: string | null) => (phaseId ? phases.find((p) => p.id === phaseId)?.name ?? "" : ""), [phases])

  // ---- persisted task update (drawer edits, drag-end, status/progress sync, cascade) ----
  // Status<->Progress sync (spec #5/#11) lives HERE, once, so every caller
  // (Gantt drawer, List View inline edit, bulk actions) gets it for free
  // instead of re-checking the condition at each call site.
  //
  // Working-day duration (spec #5-#7): `duration` means WORKING days, and
  // this is the ONE place that reconciles it against startDate/endDate for
  // every caller (drag-move, resize, Start field, Duration field, cascade).
  // Rule, applied once here instead of at each call site:
  //   - duration given                    -> endDate derived from it
  //   - endDate given, startDate not      -> resize: duration derived from it
  //   - startDate given, endDate not      -> move: endDate re-derived to
  //                                          preserve the task's existing
  //                                          working-day duration
  //   - both startDate and endDate given  -> duration derived to match
  //     (e.g. a cascade/bulk shift that already computed both explicitly)
  const updateTask = useCallback(
    (id: string, rawPatch: Partial<GanttTask>) => {
      const patch = { ...rawPatch }
      if (patch.status === "completed" && patch.progress === undefined) patch.progress = 100
      if (patch.progress === 100 && patch.status === undefined) patch.status = "completed"
      if (patch.progress !== undefined && patch.progress < 100 && patch.status === undefined) {
        const current = tasksById.get(id)
        if (current?.status === "completed") patch.status = "in-progress"
      }

      const current = tasksById.get(id)
      if (current) {
        if (patch.duration !== undefined) {
          const start = toDate(patch.startDate ?? current.startDate)
          patch.endDate = toDateStr(addWorkingDays(start, patch.duration, workingDays))
        } else if (patch.endDate !== undefined && patch.startDate === undefined) {
          patch.duration = countWorkingDays(toDate(current.startDate), toDate(patch.endDate), workingDays)
        } else if (patch.startDate !== undefined && patch.endDate === undefined) {
          patch.endDate = toDateStr(addWorkingDays(toDate(patch.startDate), current.duration, workingDays))
        } else if (patch.startDate !== undefined && patch.endDate !== undefined) {
          patch.duration = countWorkingDays(toDate(patch.startDate), toDate(patch.endDate), workingDays)
        }
      }

      setTasks((prev) => {
        const next = prev.map((t) => (t.id === id ? { ...t, ...patch } : t))
        if (patch.startDate || patch.endDate) {
          // Full recompute (not incremental) -- diffing against `prev`
          // (not `next`) catches BOTH the directly-edited task and any
          // successor recomputeSchedule moved, in one pass. `id` is
          // pinned: this task's date was just set explicitly (drag,
          // resize, or a manual Start/Duration field edit) and must
          // stick, even if it also happens to have its own incoming
          // dependency -- only its SUCCESSORS should cascade.
          const recomputed = recomputeSchedule(next, dependencies, workingDays, new Set([id]))
          const changed = recomputed.filter((t) => {
            const before = prev.find((p) => p.id === t.id)
            return before && (before.startDate !== t.startDate || before.endDate !== t.endDate)
          })
          persistTasks(changed)
          return recomputed
        }
        const updated = next.find((t) => t.id === id)
        if (updated) persistTasks([updated])
        return next
      })
    },
    [dependencies, persistTasks, tasksById, workingDays]
  )

  // ---- create / delete -----------------------------------------------------
  const addPhase = useCallback(() => {
    if (!projectId || !companyId || !userId) return
    const start = new Date()
    const duration = 7
    const newPhase: GanttTask = {
      id: crypto.randomUUID(),
      name: "New Phase",
      type: "group",
      startDate: toDateStr(start),
      endDate: toDateStr(addWorkingDays(start, duration, workingDays)),
      duration,
      progress: 0,
      status: "not-started",
      color: PHASE_PALETTE[phases.length % PHASE_PALETTE.length],
      sortOrder: phases.length,
      level: 0,
      isExpanded: true,
    }
    setTasks((prev) => [...prev, newPhase])
    saveGanttTask(projectId, newPhase, companyId, userId).catch((err) => console.error("[Gantt] addPhase failed", err))
    return newPhase
  }, [projectId, companyId, userId, phases.length, workingDays])

  const addTask = useCallback(
    (phaseId: string) => {
      if (!projectId || !companyId || !userId || !phaseId) return
      const siblingCount = (childrenByPhase.get(phaseId) || []).length
      const start = new Date()
      const duration = 2
      const newTask: GanttTask = {
        id: crypto.randomUUID(),
        name: "New Task",
        type: "task",
        parentId: phaseId,
        startDate: toDateStr(start),
        endDate: toDateStr(addWorkingDays(start, duration, workingDays)),
        duration,
        progress: 0,
        status: "not-started",
        color: phaseColor(phaseId),
        sortOrder: siblingCount,
        level: 1,
      }
      setTasks((prev) => [...prev, newTask])
      saveGanttTask(projectId, newTask, companyId, userId).catch((err) => console.error("[Gantt] addTask failed", err))
      return newTask
    },
    [projectId, companyId, userId, childrenByPhase, phaseColor, workingDays]
  )

  const deleteTask = useCallback((id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id))
    setDependencies((prev) => prev.filter((d) => d.fromTaskId !== id && d.toTaskId !== id))
    deleteGanttTask(id).catch((err) => console.error("[Gantt] deleteTask failed", err))
  }, [])

  const deletePhase = useCallback(
    (id: string) => {
      const children = childrenByPhase.get(id) || []
      setTasks((prev) => prev.filter((t) => t.id !== id && t.parentId !== id))
      const childIds = new Set(children.map((c) => c.id))
      setDependencies((prev) => prev.filter((d) => !childIds.has(d.fromTaskId) && !childIds.has(d.toTaskId) && d.fromTaskId !== id && d.toTaskId !== id))
      deleteGanttTask(id).catch((err) => console.error("[Gantt] deletePhase failed", err))
      for (const c of children) deleteGanttTask(c.id).catch((err) => console.error("[Gantt] deletePhase child failed", err))
    },
    [childrenByPhase]
  )

  const renamePhase = useCallback(
    (phaseId: string, newName: string) => {
      if (!newName.trim()) return
      updateTask(phaseId, { name: newName })
    },
    [updateTask]
  )

  // ---- dependencies ---------------------------------------------------------
  // Returns false (and creates nothing) when fromTaskId===toTaskId or the
  // new edge would close a cycle (spec #7) -- callers show the existing
  // FloatingAlert on a false return instead of a new error UI.
  const addDependency = useCallback(
    (fromTaskId: string, toTaskId: string, type: GanttDependency["type"], lagDays: number): boolean => {
      if (!projectId || !companyId || !userId || fromTaskId === toTaskId) return false
      if (wouldCreateCycle(dependencies, fromTaskId, toTaskId)) return false
      const dep: GanttDependency = { id: crypto.randomUUID(), fromTaskId, toTaskId, type, lagDays }
      setDependencies((prev) => [...prev, dep])
      createGanttDependency(projectId, dep, companyId, userId)
        .then((ok) => {
          if (!ok) setDependencies((prev) => prev.filter((d) => d.id !== dep.id))
        })
        .catch((err) => console.error("[Gantt] addDependency failed", err))
      setTasks((prev) => {
        const recomputed = recomputeSchedule(prev, [...dependencies, dep], workingDays)
        const changed = recomputed.filter((t) => {
          const before = prev.find((p) => p.id === t.id)
          return before && (before.startDate !== t.startDate || before.endDate !== t.endDate)
        })
        if (changed.length) persistTasks(changed)
        return recomputed
      })
      return true
    },
    [projectId, companyId, userId, dependencies, persistTasks, workingDays]
  )

  // Editing an existing dependency's type/lag (spec: "Depends on" editor) --
  // same optimistic-update + recompute shape as addDependency, just a patch
  // instead of a new row. from/to never change here (that's a different
  // dependency, handled by remove + add).
  const updateDependency = useCallback(
    (dep: GanttDependency, patch: { type?: GanttDependency["type"]; lagDays?: number }) => {
      const updatedDep: GanttDependency = { ...dep, ...patch }
      setDependencies((prev) => prev.map((d) => (d.id === dep.id ? updatedDep : d)))
      if (projectId && companyId && userId) {
        updateGanttDependency(dep, patch, companyId, userId).catch((err) => console.error("[Gantt] updateDependency failed", err))
      }
      setTasks((prev) => {
        const nextDeps = dependencies.map((d) => (d.id === dep.id ? updatedDep : d))
        const recomputed = recomputeSchedule(prev, nextDeps, workingDays)
        const changed = recomputed.filter((t) => {
          const before = prev.find((p) => p.id === t.id)
          return before && (before.startDate !== t.startDate || before.endDate !== t.endDate)
        })
        if (changed.length) persistTasks(changed)
        return recomputed
      })
    },
    [projectId, companyId, userId, dependencies, persistTasks, workingDays]
  )

  const removeDependency = useCallback(
    (dep: GanttDependency) => {
      setDependencies((prev) => prev.filter((d) => d.id !== dep.id))
      if (!projectId || !companyId || !userId) return
      deleteGanttDependency(projectId, dep, companyId, userId).catch((err) => console.error("[Gantt] removeDependency failed", err))
    },
    [projectId, companyId, userId]
  )

  // ---- row drag-and-drop reorder (persists sortOrder + parentId) --------------
  const reorderSiblings = useCallback(
    (kind: "phase" | "task", draggedId: string, targetId: string, targetParentId: string | null) => {
      setTasks((prev) => {
        const siblings = prev
          .filter((t) => (kind === "phase" ? t.type === "group" : t.type === "task" && t.parentId === targetParentId))
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        const working = [...siblings]
        const draggedIdx = working.findIndex((t) => t.id === draggedId)
        let dragged: GanttTask
        if (draggedIdx === -1) {
          const found = prev.find((t) => t.id === draggedId)
          if (!found) return prev
          dragged = { ...found, parentId: targetParentId ?? undefined, level: kind === "task" ? 1 : 0 }
          working.push(dragged)
        } else {
          dragged = working[draggedIdx]
          working.splice(draggedIdx, 1)
        }
        const targetIdx = working.findIndex((t) => t.id === targetId)
        working.splice(targetIdx === -1 ? working.length : targetIdx, 0, dragged)
        const reindexed = working.map((t, i) => ({ ...t, sortOrder: i }))
        persistTasks(reindexed)
        const reindexedIds = new Set(reindexed.map((t) => t.id))
        return [...prev.filter((t) => !reindexedIds.has(t.id)), ...reindexed]
      })
    },
    [persistTasks]
  )

  // ---- bulk actions (multi-select) ------------------------------------------
  const bulkShiftDates = useCallback(
    (taskIds: string[], deltaDays: number) => {
      if (deltaDays === 0 || taskIds.length === 0) return
      setTasks((prev) => {
        const idSet = new Set(taskIds)
        const shifted = prev.map((t) => {
          if (!idSet.has(t.id)) return t
          const newStart = daysFrom(toDate(t.startDate), deltaDays)
          return { ...t, startDate: toDateStr(newStart), endDate: toDateStr(addWorkingDays(newStart, t.duration, workingDays)) }
        })
        // Same pinning as updateTask -- every bulk-shifted task must land
        // exactly where the shift put it, even if one of them also has
        // its own incoming dependency.
        const recomputed = recomputeSchedule(shifted, dependencies, workingDays, new Set(taskIds))
        const changed = recomputed.filter((t) => {
          const before = prev.find((p) => p.id === t.id)
          return before && (before.startDate !== t.startDate || before.endDate !== t.endDate)
        })
        persistTasks(changed)
        return recomputed
      })
    },
    [dependencies, persistTasks, workingDays]
  )

  const bulkMoveToPhase = useCallback(
    (taskIds: string[], targetPhaseId: string) => {
      setTasks((prev) => {
        const existingSiblingCount = prev.filter((t) => t.type === "task" && t.parentId === targetPhaseId && !taskIds.includes(t.id)).length
        let offset = 0
        const moved = prev.map((t) => {
          if (t.type !== "task" || !taskIds.includes(t.id)) return t
          const updated = { ...t, parentId: targetPhaseId, sortOrder: existingSiblingCount + offset }
          offset += 1
          return updated
        })
        persistTasks(moved.filter((t) => taskIds.includes(t.id)))
        return moved
      })
    },
    [persistTasks]
  )

  const bulkDeleteTasks = useCallback(
    (taskIds: string[]) => {
      const idSet = new Set(taskIds)
      setTasks((prev) => prev.filter((t) => !idSet.has(t.id)))
      setDependencies((prev) => prev.filter((d) => !idSet.has(d.fromTaskId) && !idSet.has(d.toTaskId)))
      for (const id of taskIds) deleteGanttTask(id).catch((err) => console.error("[Gantt] bulkDeleteTasks failed", err))
    },
    []
  )

  const bulkCopyTasks = useCallback(
    (taskIds: string[]) => {
      if (!projectId || !companyId || !userId) return
      setTasks((prev) => {
        const toCopy = prev.filter((t) => taskIds.includes(t.id) && t.type === "task")
        const copies = toCopy.map((t) => ({
          ...t,
          id: crypto.randomUUID(),
          name: `${t.name} (copy)`,
          sortOrder: (t.sortOrder ?? 0) + 0.5,
          baselineStartDate: null,
          baselineEndDate: null,
        }))
        for (const copy of copies) saveGanttTask(projectId, copy, companyId, userId).catch((err) => console.error("[Gantt] bulkCopyTasks failed", err))
        return [...prev, ...copies]
      })
    },
    [projectId, companyId, userId]
  )

  // ---- baseline (spec #2: snapshot start/end, never shown as a bar) ----------
  const setBaseline = useCallback(
    (taskIds: string[]) => {
      setTasks((prev) => {
        const idSet = new Set(taskIds)
        const updated = prev.map((t) => (idSet.has(t.id) ? { ...t, baselineStartDate: t.startDate, baselineEndDate: t.endDate } : t))
        persistTasks(updated.filter((t) => idSet.has(t.id)))
        return updated
      })
    },
    [persistTasks]
  )

  return {
    tasks,
    dependencies,
    loading,
    tasksById,
    phases,
    childrenByPhase,
    phaseSummaries,
    phaseColor,
    phaseName,
    updateTask,
    addPhase,
    addTask,
    deleteTask,
    deletePhase,
    renamePhase,
    addDependency,
    updateDependency,
    removeDependency,
    reorderSiblings,
    bulkShiftDates,
    bulkMoveToPhase,
    bulkDeleteTasks,
    bulkCopyTasks,
    setBaseline,
  }
}

function daysFrom(d: Date, n: number): Date {
  const copy = new Date(d)
  copy.setDate(copy.getDate() + n)
  return copy
}

export type UseGanttData = ReturnType<typeof useGanttData>
